/**
 * src/pages/CompanySettings.jsx — Call It In LLC — Company-Wide Pricing Configuration
 *
 * Administrator-only. Manages the pricing defaults used by the task-creation
 * estimator: base rates, per-mile rates, fuel surcharge, weight rules,
 * wait-time rules, and special service rates.
 *
 * Reads and writes via getRates / saveRates (src/utils/rates.js) which
 * persist to localStorage["ci_rates"]. The task-creation form reads from
 * the same store on every mount, so changes take effect on the next
 * task form load.
 *
 * Driver and employee management is intentionally excluded here.
 * Use the Employees page for user and driver account management.
 */

import React, { useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "layouts/dashboards/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getRates, saveRates } from "../utils/rates";

// ── Field definitions ─────────────────────────────────────────────────────────
// Each group has a heading and a list of fields.
// type: "currency" | "number" | "percent" | "boolean"

const RATE_GROUPS = [
  {
    heading: "Base Pricing",
    description: "Fixed and per-distance charges applied to every trip.",
    fields: [
      { key: "base",              label: "Base Rate",                   unit: "$ / trip",  type: "currency" },
      { key: "perMile",           label: "Per-Mile Rate",               unit: "$ / mi",    type: "currency" },
      { key: "mileageFreeMiles",  label: "Free Miles (included)",       unit: "miles",     type: "number"   },
      { key: "deadheadPerMile",   label: "Deadhead Rate",               unit: "$ / mi",    type: "currency" },
      { key: "deadheadFreeMiles", label: "Deadhead Free Miles",         unit: "miles",     type: "number"   },
    ],
  },
  {
    heading: "Fuel Surcharge",
    description: "Fuel cost applied as a percentage of the billable trip amount.",
    fields: [
      { key: "fuelPct",            label: "Fuel Surcharge",              unit: "% of trip", type: "percent"  },
      { key: "includeMilesInFuel", label: "Include Miles in Fuel Base",  unit: null,        type: "boolean"  },
    ],
  },
  {
    heading: "Weight Rules",
    description: "Additional charge per pound when cargo exceeds the threshold.",
    fields: [
      { key: "weightThreshold",  label: "Weight Threshold",              unit: "lbs",       type: "number"   },
      { key: "weightPerLbOver",  label: "Charge Per lb Over Threshold",  unit: "$ / lb",    type: "currency" },
    ],
  },
  {
    heading: "Wait-Time Rules",
    description: "Charge applied after the driver waits beyond the grace period.",
    fields: [
      { key: "waitGraceMinutes", label: "Grace Period (free)",           unit: "minutes",   type: "number"   },
      { key: "waitPerMinute",    label: "Charge After Grace",            unit: "$ / min",   type: "currency" },
    ],
  },
  {
    heading: "Special Service Rates",
    description: "Flat or hourly rates for non-standard service types.",
    fields: [
      { key: "afterHoursFlat",   label: "After-Hours Flat Rate",         unit: "$ / trip",  type: "currency" },
      { key: "holdoverPerNight", label: "Holdover Rate",                 unit: "$ / night", type: "currency" },
      { key: "dedicatedPerHour", label: "Dedicated Service Rate",        unit: "$ / hr",    type: "currency" },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function toDisplay(value, type) {
  if (type === "percent") return value != null ? String(Math.round(value * 100)) : "";
  if (type === "boolean") return Boolean(value);
  return value != null ? String(value) : "";
}

function fromInput(raw, type) {
  if (type === "percent") {
    const n = parseFloat(raw);
    return isNaN(n) ? 0 : n / 100;
  }
  if (type === "boolean") return Boolean(raw);
  const n = parseFloat(raw);
  return isNaN(n) ? 0 : n;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function RateGroup({ group, rates, onChange }) {
  return (
    <Card sx={{ mb: 3 }}>
      <MDBox p={3}>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E293B" }}>
          {group.heading}
        </MDTypography>
        {group.description && (
          <MDTypography variant="caption" sx={{ color: "#64748b", display: "block", mb: 2 }}>
            {group.description}
          </MDTypography>
        )}
        <Grid container spacing={2}>
          {group.fields.map(({ key, label, unit, type }) => {
            const value = rates[key];

            if (type === "boolean") {
              return (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(value)}
                        onChange={(e) => onChange(key, e.target.checked, type)}
                        color="info"
                      />
                    }
                    label={
                      <MDTypography variant="button" sx={{ color: "#334155" }}>
                        {label}
                      </MDTypography>
                    }
                  />
                </Grid>
              );
            }

            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <MDBox position="relative">
                  <MDInput
                    fullWidth
                    type="number"
                    label={label}
                    value={toDisplay(value, type)}
                    onChange={(e) => onChange(key, e.target.value, type)}
                    inputProps={{ step: type === "currency" ? "0.01" : "1", min: "0" }}
                  />
                  {unit && (
                    <MDTypography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#94a3b8",
                        pointerEvents: "none",
                      }}
                    >
                      {unit}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
            );
          })}
        </Grid>
      </MDBox>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CompanySettings() {
  const [rates, setRates] = useState(() => getRates());
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState({ open: false, color: "success", title: "", content: "" });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const handleChange = (key, rawValue, type) => {
    const parsed = type === "boolean" ? Boolean(rawValue) : fromInput(rawValue, type);
    setRates((prev) => ({ ...prev, [key]: parsed }));
    setDirty(true);
  };

  const handleSave = () => {
    saveRates(rates);
    setDirty(false);
    setToast({
      open: true,
      color: "success",
      title: "Saved",
      content: "Company pricing configuration updated. Changes take effect on the next task form load.",
    });
  };

  const handleReset = () => {
    setRates(getRates());
    setDirty(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={2} sx={{ maxWidth: 900, mx: "auto" }}>
        {/* Page header */}
        <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#1E293B" }}>
              Company Settings
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#475569", mt: 0.5 }}>
              Company-wide pricing defaults. Changes apply to all new task estimates on next form load.
              Driver and employee accounts are managed under{" "}
              <MDTypography
                component="a"
                href="#/employees"
                variant="body2"
                sx={{ color: "#3b82f6", fontWeight: 600 }}
              >
                Employees
              </MDTypography>
              .
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1.5} flexShrink={0}>
            {dirty && (
              <MDButton variant="outlined" color="secondary" onClick={handleReset} size="small">
                Discard Changes
              </MDButton>
            )}
            <MDButton variant="gradient" color="info" onClick={handleSave}>
              Save Settings
            </MDButton>
          </MDBox>
        </MDBox>

        <Divider sx={{ mb: 3 }} />

        {/* Rate groups */}
        {RATE_GROUPS.map((group) => (
          <RateGroup
            key={group.heading}
            group={group}
            rates={rates}
            onChange={handleChange}
          />
        ))}

        {/* Bottom save bar */}
        <MDBox display="flex" justifyContent="flex-end" gap={1.5} mt={1}>
          {dirty && (
            <MDButton variant="outlined" color="secondary" onClick={handleReset}>
              Discard Changes
            </MDButton>
          )}
          <MDButton variant="gradient" color="info" onClick={handleSave}>
            Save Settings
          </MDButton>
        </MDBox>
      </MDBox>

      <MDSnackbar
        color={toast.color}
        icon="check"
        title={toast.title}
        content={toast.content}
        open={toast.open}
        onClose={closeToast}
        close={closeToast}
        bgWhite
      />
    </DashboardLayout>
  );
}
